import GuiContext from "config/context/guiContext";
import ToastContext from "config/context/toastContext";
import useClipboard from "config/useClipboard";
import useCustomStyles from "config/useCustomStyles";
import useTheme from "config/useTheme";
import styles from "css/privateKey.module.css";
import styles2 from "css/addNewDatabase.module.css";
import PropTypes from "prop-types";
import { useContext, useState, useEffect } from "react";
import Modal from "react-modal";
import Button from "../shared/button/Button";
import InputField from "components/shared/input-field/InputField";
import { useGenerateKeypair } from "api/authentication/usePrivateKey";
import downloadFile from "lib/DownloadFile";
import { ExportIcon } from "assets/icons";
import ActionLoader from "components/shared/status/ActionLoader";
import PrivateKeyField from "components/shared/private-key-field/PrivateKeyField";

const AddNewAccount = ({ typeCall, openModal, setOpenModal }) => {
  const [creatorName, setCreatorName] = useState("");
  const [newPublicKey, setNewPublicKey] = useState("");
  const [newPrivateKey, setNewPrivateKey] = useState("");
  const [firstInput, addFirstInput] = useState("");
  const [lastInput, addLastInput] = useState("");
  const [serverMessage, setServerMessage] = useState("");
  const [success, setSuccess] = useState(false);
  const {
    userData,
    selected: { selectedAccount, selectedServer },
    updateAccounts,
    removeAccount,
    URL,
  } = useContext(GuiContext);
  const { dark } = useTheme();
  const { insertIntoToastList } = useContext(ToastContext);
  const { writeClipboard } = useClipboard();
  const [mode, setMode] = useState("start");
  const [checked, setChecked] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [newAccountData, setNewAccountData] = useState(null);
  const customStyles = useCustomStyles();

  const {
    response: keypairResponse,
    loading: keypairLoading,
    error: keypairError,
    getKeypair,
  } = useGenerateKeypair();

  useEffect(() => {
    if (keypairResponse?.privateKey) {
      setNewPrivateKey(keypairResponse.privateKey);
      setChecked(false); // Reset checkbox when new keys are generated
    }
    if (keypairResponse?.publicKey) {
      setNewPublicKey(keypairResponse.publicKey);
      setChecked(false); // Reset checkbox when new keys are generated
    }
  }, [keypairResponse]);

  const handleExportKeys = () => {
    if (newPublicKey && newPrivateKey) {
      const content = `Public Key:\n${newPublicKey}\n\nPrivate Key:\n${newPrivateKey}`;
      const filename = `database_keys_${Date.now()}.txt`;
      downloadFile(content, filename, { type: 'text/plain;charset=utf-8;' });
    }
  };

  const resetForm = () => {
    addFirstInput("");
    addLastInput("");
  };
  const handleCancel = () => {
    addFirstInput("");
    addLastInput("");
    setCreatorName("");
    setNewPublicKey("");
    setNewPrivateKey("");
    setServerMessage("");
    setMode("start");
    setOpenModal(false);
    setChecked(false);
    setSuccess(false);
  };

  const handleSubmit = () => {
    setServerMessage("");
    let url = "";

    const data = {};

    if (mode === "createNew") {
      url = `${URL}/api/account/create_account`;
      data.guiname = userData.guiname;
      data.public_key = newPublicKey;
      data.creator_name = creatorName;
      data.server_url = selectedServer;
      data.account_name = firstInput;
      data.creator_key = lastInput;
      data.import = true;
    } else {
      url = `${URL}/api/inerygui/${typeCall}`;
      data.guiname = userData.guiname;
      data.server_url = selectedServer;
      data.account_name = firstInput;
      data.private_key = lastInput;
    }

    async function sendDataToServer() {
      setIsSubmitting(true);
      try {
        const response = await fetch(url, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(data),
        });
        const res = await response.json();
        if (response.ok && res.status === true) {
          setServerMessage("");
          setSuccess(true);
          switch (typeCall) {
            case "add_account":
              if (mode === "import") {
                updateAccounts(firstInput);
                insertIntoToastList(
                  "Database is imported",
                  "success",
                  "importAccount",
                );
                setMode("account_imported");
              } else {
                insertIntoToastList(
                  "Database is created",
                  "success",
                  "addAccount",
                );
                setNewAccountData(res);
                updateAccounts(firstInput);
                setMode("account_created");
              }
              break;
            case "remove_account":
              removeAccount(firstInput);
              insertIntoToastList(
                "Database is removed",
                "success",
                "removeAccount",
              );
              setMode("account_removed");
              break;
            default:
              break;
          }
        } else {
          let errorMessageFromServer = "";
          switch (typeCall) {
            case "add_account":
              if (mode === "createNew") {
                errorMessageFromServer = "Transaction is not authorized";
                setServerMessage(errorMessageFromServer);
              } else {
                errorMessageFromServer = "Unable to import database";
                insertIntoToastList(errorMessageFromServer, "error", "addAccount");
              }
              break;
            case "remove_account":
              errorMessageFromServer = "Unable to remove database";
              insertIntoToastList(errorMessageFromServer, "error", "addAccount");
              break;
            case "createNew":
              errorMessageFromServer = "Transaction is not authorized";
              setServerMessage(errorMessageFromServer);
              break;
            default:
              break;
          }
          setSuccess(false);
        }
      } catch (error) {
        console.error(error, "greska");
        if (mode === "createNew") {
          setServerMessage("Transaction is not authorized");
          setSuccess(false);
        }
      } finally {
        setIsSubmitting(false);
      }
    }
    sendDataToServer();
  };

  const renderModalContent = (typeCall, mode) => {
    switch (typeCall) {
      case "add_account":
        switch (mode) {
          case "start":
            return (
              <>
                <h2>Add New Database</h2>
                <div className="container column gap-1 align-center">
                  <div className="container column gap-05">
                    <Button onClick={() => {
                      setServerMessage("");
                      setMode("import");
                    }} expand="full">
                      Import Database
                    </Button>
                    <Button
                      onClick={() => {
                        setServerMessage("");
                        setChecked(false);
                        setMode("createNew");
                      }}
                      expand="full"
                      variant="secondary"
                    >
                      Add Database
                    </Button>
                  </div>
                  <Button
                    onClick={handleCancel}
                    buttonType="text"
                    variant="secondary"
                  >
                    Cancel
                  </Button>
                </div>
              </>
            );
          case "import":
            return (
              <>
                <h2>Import database</h2>
                <InputField
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  type="text"
                  placeholder="Database Name"
                />
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Private Key"
                />
                <div className="column align-center gap-05">
                  <Button onClick={handleSubmit}>Confirm</Button>
                  <Button buttonType="text" onClick={handleCancel}>
                    Cancel
                  </Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
                {isSubmitting && <ActionLoader />}
              </>
            );
          case "createNew":
            return (
              <>
                <h2>Add new database</h2>
                <InputField
                  value={creatorName}
                  onChange={(e) => setCreatorName(e.target.value)}
                  type="text"
                  placeholder="Creator Name"
                />
                <InputField
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  type="text"
                  placeholder="Database Name"
                />
                <div className={styles2.generate_keypair}>
                  <button
                    type="button"
                    className={styles2.generate_keypair_btn}
                    onClick={() => getKeypair()}
                    disabled={keypairLoading}
                  >
                    {keypairLoading ? "Generating..." : "Generate keypair"}
                  </button>

                  {newPublicKey && (
                    <p className={styles2.generated_key}>Public Key: {newPublicKey}</p>
                  )}
                  {newPrivateKey && (
                    <div className={styles2.generated_key_wrapper}>
                      <p className={styles2.generated_key}>
                        Private Key: {newPrivateKey.substring(0, 3)}...
                      </p>
                      {(newPublicKey && newPrivateKey) && (
                        <button 
                          type="button" 
                          className={styles2.export_button}
                          onClick={handleExportKeys}
                          title="Export keys to text file"
                        >
                          <ExportIcon />
                        </button>
                      )}
                    </div>
                  )}
                  {keypairError && (
                    <p className={styles.error}>{keypairError}</p>
                  )}
                </div>
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Creator Private Key"
                />
                {(newPublicKey && newPrivateKey) && (
                  <p className={styles2.checkbox_text} onClick={() => setChecked((prev) => !prev)}>
                    <input 
                      checked={checked} 
                      type="checkbox" 
                      onChange={(e) => setChecked(e.target.checked)}
                    /> I have saved the keys
                  </p>
                )}
                <div className="column align-center gap-05">
                  <Button 
                    onClick={handleSubmit}
                    disabled={
                      !creatorName.trim() || 
                      !firstInput.trim() || 
                      !lastInput.trim() || 
                      ((newPublicKey && newPrivateKey) && !checked)
                    }
                  >
                    Confirm
                  </Button>
                  <Button
                    buttonType="text"
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
                {isSubmitting && <ActionLoader />}
              </>
            );
          case "account_created":
            return (
              <>
                <div className={styles.text}>
                  <h2>Database "{firstInput}" is successfully created</h2>
                </div>
                  <div className={styles.btnGroup}>
                    <Button onClick={handleCancel}>OK</Button>
                  </div>
              </>
            );
          case "account_imported":
            return (
              <>
                <div className={styles.text}>
                  <h2>Database {firstInput} is imported</h2>
                </div>

                <div className={styles.btnGroup}>
                  <Button onClick={handleCancel}>OK</Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {" "}
                    {serverMessage}
                  </p>
                )}
              </>
            );
          default:
            return null;
        }
      case "remove_account":
        switch (mode) {
          case "start":
            return (
              <>
                <div className={styles.text}>
                  <h2>Remove Database</h2>
                </div>
                <input
                  value={firstInput}
                  onChange={(e) => addFirstInput(e.target.value)}
                  type="text"
                  placeholder="Enter database name"
                />
                <PrivateKeyField
                  value={lastInput}
                  onChange={(e) => addLastInput(e.target.value)}
                  placeholder="Enter your private key"
                />
                <div className={styles.btnGroup}>
                  <Button onClick={handleSubmit}>Remove</Button>
                  <Button
                    buttonType="text"
                    variant="secondary"
                    onClick={handleCancel}
                  >
                    Cancel
                  </Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
                {isSubmitting && <ActionLoader />}
              </>
            );
          case "account_removed":
            return (
              <>
                <div className={styles.text}>
                  <h2>Database {firstInput} is removed</h2>
                </div>

                <div className={styles.btnGroup}>
                  <Button onClick={handleCancel}>OK</Button>
                </div>
                {serverMessage && (
                  <p className={success ? styles.success : styles.error}>
                    {serverMessage}
                  </p>
                )}
              </>
            );

          default:
            return null;
        }

      default:
        return null;
    }
  };

  return (
    <Modal
      isOpen={openModal}
      ariaHideApp={false}
      // onAfterOpen={afterOpenModal}
      // onRequestClose={closeModal}
      style={customStyles}
      contentLabel="Modal"
    >
      <div className={`${styles.privateKey}  ${dark ? styles.dark : ""}`} style={{ position: 'relative' }}>
        {renderModalContent(typeCall, mode)}
      </div>
    </Modal>
  );
};

export default AddNewAccount;

AddNewAccount.propTypes = {
  type: PropTypes.string,
  typeCall: PropTypes.string,
};
